import type { ServiceType } from '@rover/rsdk/src/apiClient/latest/model/serviceType';
import type { SpecialtyServiceOptionsEnum } from '@rover/rsdk/src/apiClient/latest/model/specialtyServiceOptionsEnum';
import { captureError } from '@rover/rsdk/src/modules/ErrorReporting';
import type { ServiceTypeOption, ServiceTypeSlug } from '@rover/types/src/ServiceType';

import type { ServiceSelectProps } from './ServiceSelect';

export type BuildServiceSelectPropsArgs = {
  pageServiceTypeSlug: SpecialtyServiceOptionsEnum;
  serviceTypes: ServiceType[];
  specialtyServiceOptions: SpecialtyServiceOptionsEnum[];
};

// Currently, the training and grooming search pages only return dogs.
const SUPPORTED_SUGGESTED_PET_TYPES = new Set(['any', 'dog']);

const toOption = ({ slug, displayName, description }: ServiceType): ServiceTypeOption => ({
  title: displayName,
  subtitle: description,
  value: slug as ServiceTypeSlug,
});

const buildServiceSelectProps = ({
  pageServiceTypeSlug,
  serviceTypes,
  specialtyServiceOptions,
}: BuildServiceSelectPropsArgs): ServiceSelectProps | null => {
  if (!specialtyServiceOptions.includes(pageServiceTypeSlug)) return null;

  const specialtyServiceTypesBySlug = new Map(
    specialtyServiceOptions.flatMap((slug) => {
      const serviceType = serviceTypes.find((st) => st.slug === slug);
      return serviceType ? [[slug, serviceType] as const] : [];
    })
  );

  const currentServiceType = specialtyServiceTypesBySlug.get(pageServiceTypeSlug);

  if (!currentServiceType) {
    captureError(
      new Error('Cross-vertical service select: page service type missing from service types'),
      { errorInfo: { pageServiceTypeSlug } }
    );
    return null;
  }

  const browsableServiceTypes = serviceTypes.filter(
    ({ slug, browsable, suggestedPetType }) =>
      browsable &&
      slug !== pageServiceTypeSlug &&
      SUPPORTED_SUGGESTED_PET_TYPES.has(suggestedPetType ?? 'any')
  );

  return {
    currentOption: toOption(currentServiceType),
    serviceTypeOptions: [...browsableServiceTypes, ...specialtyServiceTypesBySlug.values()].map(
      toOption
    ),
  };
};

export default buildServiceSelectProps;
